import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import { PropsWithChildrenRequired } from '../../../types/PropsWithChildrenRequired'

interface Props {
    maxLines?: number
    className?: string
    /**
     * Optional explicit max height (in px) to clamp non-text/flex content.
     * Useful when line-clamp cannot apply (e.g. flex-wrapped badges).
     */
    maxHeightPx?: number
    /**
     * Optional count of hidden items to display in the "see more" label.
     */
    hiddenCount?: number
}

export default function ExpandableText({
    children,
    maxLines,
    className,
    maxHeightPx,
    hiddenCount,
}: PropsWithChildrenRequired<Props>): React.JSX.Element {
    const [expanded, setExpanded] = useState(false)
    const [isOverflowing, setIsOverflowing] = useState(false)
    const textRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const el = textRef.current
        if (el && !expanded) {
            requestAnimationFrame(() => {
                setIsOverflowing(el.scrollHeight > el.clientHeight)
            })
        }
    }, [children, maxLines, expanded])

    return (
        <div className={clsx('', className)}>
            <div
                ref={textRef}
                className={clsx({
                    [`line-clamp-${maxLines}`]: !expanded,
                    'overflow-hidden': !expanded,
                })}
                style={
                    !expanded && maxHeightPx
                        ? { maxHeight: `${maxHeightPx}px` }
                        : undefined
                }
            >
                {children}
            </div>

            {isOverflowing && (
                <button
                    onClick={() => setExpanded((prev) => !prev)}
                    className="text-blue-600 hover:text-blue-600 underline mt-1"
                >
                    {expanded
                        ? '... show less'
                        : hiddenCount && hiddenCount > 0
                          ? `... see ${hiddenCount} more`
                          : '... see more'}
                </button>
            )}
        </div>
    )
}
